import { useEffect, useState } from 'react';
import { Plus, MoreVertical, Calendar, DollarSign, User, X, Pencil, Trash2, ArrowRight } from 'lucide-react';
import api, { Deal, Lead } from '../services/api';

const stageConfig = [
  { name: 'Lead', key: 'lead', color: '#3b82f6' },
  { name: 'Qualified', key: 'qualified', color: '#8b5cf6' },
  { name: 'Proposal', key: 'proposal', color: '#f59e0b' },
  { name: 'Negotiation', key: 'negotiation', color: '#10b981' },
  { name: 'Closed Won', key: 'closed-won', color: '#22c55e' },
  { name: 'Closed Lost', key: 'closed-lost', color: '#ef4444' },
];

function AddDealModal({ leads, onClose, onSave }: { leads: Lead[]; onClose: () => void; onSave: (deal: Omit<Deal, 'id' | 'createdAt'>) => void }) {
  const [formData, setFormData] = useState({
    title: '',
    value: 0,
    stage: 'lead' as Deal['stage'],
    probability: 10,
    leadId: '',
    expectedClose: '',
  });

  const handleSubmit = () => {
    const lead = leads.find(l => l.id === formData.leadId);
    if (!lead || !formData.title || !formData.expectedClose) return;
    
    onSave({
      title: formData.title,
      value: formData.value,
      stage: formData.stage,
      probability: formData.probability,
      lead,
      expectedClose: formData.expectedClose,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold">Add New Deal</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter deal title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Associated Lead</label>
            <select
              value={formData.leadId}
              onChange={(e) => setFormData({ ...formData, leadId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a lead</option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>{lead.name} - {lead.company}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value ($)</label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Probability (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.probability}
                onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => setFormData({ ...formData, stage: e.target.value as Deal['stage'] })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {stageConfig.map((stage) => (
                  <option key={stage.key} value={stage.key}>{stage.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close</label>
              <input
                type="date"
                value={formData.expectedClose}
                onChange={(e) => setFormData({ ...formData, expectedClose: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Create Deal</button>
        </div>
      </div>
    </div>
  );
}

function StageDetailModal({ stage, deals, leads, onClose, onMoveDeal, onDeleteDeal }: { 
  stage: typeof stageConfig[0]; 
  deals: Deal[];
  leads: Lead[];
  onClose: () => void;
  onMoveDeal: (dealId: string, newStage: string) => void;
  onDeleteDeal: (dealId: string) => void;
}) {
  const stageDeals = deals.filter(d => d.stage === stage.key);
  const otherStages = stageConfig.filter(s => s.key !== stage.key);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: stage.color }}>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: stage.color }} />
            <h2 className="text-xl font-bold">{stage.name}</h2>
            <span className="text-sm text-gray-500">({stageDeals.length} deals)</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-auto max-h-[60vh]">
          {stageDeals.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No deals in this stage</p>
          ) : (
            <div className="space-y-3">
              {stageDeals.map((deal) => (
                <div key={deal.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{deal.title}</h3>
                      <p className="text-sm text-gray-500">{deal.lead.name} - {deal.lead.company}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">${deal.value.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">{deal.probability}% probability</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-sm text-gray-500">Move to:</span>
                    <select
                      onChange={(e) => onMoveDeal(deal.id, e.target.value)}
                      className="text-sm border rounded px-2 py-1"
                      defaultValue=""
                    >
                      <option value="" disabled>Select stage</option>
                      {otherStages.map((s) => (
                        <option key={s.key} value={s.key}>{s.name}</option>
                      ))}
                    </select>
                    <button onClick={() => onDeleteDeal(deal.id)} className="ml-auto text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Pipeline() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState<typeof stageConfig[0] | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dealsData, leadsData] = await Promise.all([
          api.getDeals(),
          api.getLeads(),
        ]);
        setDeals(dealsData);
        setLeads(leadsData);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getDealsByStage = (stage: string) => deals.filter(deal => deal.stage === stage);
  const getStageValue = (stage: string) => getDealsByStage(stage).reduce((sum, deal) => sum + deal.value, 0);

  const handleAddDeal = async (dealData: Omit<Deal, 'id' | 'createdAt'>) => {
    try {
      await api.createDeal(dealData);
      const data = await api.getDeals();
      setDeals(data);
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to create deal:', error);
    }
  };

  const handleMoveDeal = async (dealId: string, newStage: string) => {
    try {
      await api.updateDeal(dealId, { stage: newStage as Deal['stage'] });
      const data = await api.getDeals();
      setDeals(data);
    } catch (error) {
      console.error('Failed to move deal:', error);
    }
  };

  const handleDeleteDeal = async (dealId: string) => {
    if (confirm('Are you sure you want to delete this deal?')) {
      try {
        await api.deleteDeal(dealId);
        setDeals(deals.filter(d => d.id !== dealId));
      } catch (error) {
        console.error('Failed to delete deal:', error);
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Pipeline</h1>
          <p className="text-gray-500 mt-1">Track and manage your deals through each stage.</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
          <Plus className="w-5 h-5" />
          Add Deal
        </button>
      </div>

      {/* Pipeline Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {stageConfig.map((stage) => {
            const stageDeals = getDealsByStage(stage.key);
            const stageValue = getStageValue(stage.key);
            
            return (
              <div key={stage.key} className="w-72 flex-shrink-0">
                {/* Stage Header - Clickable */}
                <button 
                  onClick={() => setSelectedStage(stage)}
                  className="w-full bg-white rounded-t-xl p-4 border border-gray-100 border-b-0 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                      <h3 className="font-semibold text-gray-900">{stage.name}</h3>
                    </div>
                    <span className="text-sm text-gray-500">{stageDeals.length}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-700">${stageValue.toLocaleString()}</p>
                </button>

                {/* Stage Cards */}
                <div className="bg-gray-50 rounded-b-xl p-2 min-h-[300px] space-y-2">
                  {stageDeals.map((deal) => (
                    <div key={deal.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-medium text-gray-900 text-sm">{deal.title}</h4>
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <DollarSign className="w-4 h-4" />
                          <span className="font-medium">${deal.value.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <User className="w-4 h-4" />
                          <span>{deal.lead.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>Expected: {new Date(deal.expectedClose).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Probability</span>
                        <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ backgroundColor: `${stage.color}20`, color: stage.color }}>
                          {deal.probability}%
                        </span>
                      </div>
                    </div>
                  ))}
                  {stageDeals.length === 0 && (
                    <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                      No deals - Click to add
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipeline Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pipeline Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Total Pipeline Value</p>
            <p className="text-2xl font-bold text-gray-900">${deals.reduce((sum, d) => sum + d.value, 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Weighted Value</p>
            <p className="text-2xl font-bold text-gray-900">${deals.reduce((sum, d) => sum + (d.value * d.probability / 100), 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Average Deal Size</p>
            <p className="text-2xl font-bold text-gray-900">${deals.length > 0 ? Math.round(deals.reduce((sum, d) => sum + d.value, 0) / deals.length).toLocaleString() : 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Total Deals</p>
            <p className="text-2xl font-bold text-gray-900">{deals.length}</p>
          </div>
        </div>
      </div>

      {showAddModal && <AddDealModal leads={leads} onClose={() => setShowAddModal(false)} onSave={handleAddDeal} />}
      {selectedStage && (
        <StageDetailModal 
          stage={selectedStage} 
          deals={deals} 
          leads={leads}
          onClose={() => setSelectedStage(null)} 
          onMoveDeal={handleMoveDeal}
          onDeleteDeal={handleDeleteDeal}
        />
      )}
    </div>
  );
}
